// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * The platform vocabularies of the RECORDED organisation-profile attributes
 * — Sprint 62.53.0 (owner rulings #662 and #665), and Sprint 62.57.0 Slice E
 * (work file D47) for the third.
 *
 * Each list is what an organisation may state about itself on the Organization
 * profile page, and what a pack condition over the attribute may name
 * (`REQUIREMENT_APPLICABILITY_VALUE_OUTSIDE_VOCABULARY` refuses anything else).
 *
 * ## What these lists never hold (R-46)
 *
 * An instrument number, a framework code, an authority's or institution's name,
 * or a reading of which law reaches which value. Which requirements a value
 * reaches is pack content, authored and second-read in `packs/regulatory/`, and
 * the value here is only the generic activity or role it names.
 *
 * ## `none` and `unknown` are not values
 *
 * They are states of a statement (`OrgProfileStatementState` in
 * `org-profile-statement.ts`), resolved to `stated_none` and to `not_stated`
 * with `declared_unknown` (`ProfileAttributeValue`). Keeping them out of the
 * vocabulary is what makes a pack condition naming `none` a validation error
 * rather than a condition that silently matches an answer (decision D-1).
 */
/**
 * The data exploitation routes an organisation takes part in (#665).
 *
 * Three values for participation in a State data centre, not one (decision D-3):
 * a party that shares data with the centre, or invests in or sponsors it, does
 * not exploit the centre's data, and one value would hand it the exploiter's
 * duties. `state_data_incentive_application` stays separate from the four
 * exploitation routes (decision D-2): applying for a State data incentive is an
 * activity of its own and is never read as exploitation.
 */
export const DATA_EXPLOITATION_ROUTES = [
    'public_data_access_request',
    'state_data_centre_exploitation',
    'state_data_incubator',
    'data_controlled_testing',
    'state_data_centre_data_sharing',
    'state_data_centre_investment',
    'foreign_party_data_cooperation',
    'state_data_incentive_application',
];
/**
 * The regulated-entity roles an organisation holds (#662).
 *
 * Generic institutional roles, never a named institution: a deposit insurer and
 * a central bank of the co-operative system are roles a pack may address, and
 * which duties reach each one is the pack's reading.
 */
export const REGULATED_ENTITY_TYPES = [
    'credit_cooperative',
    'cooperative_central_bank',
    'deposit_insurer',
    'microfinance_institution',
];
/**
 * Sprint 62.57.0 Slice E (work file D47) — the personal data processing
 * services an organisation provides, or intends to provide, to others as a
 * business.
 *
 * Generic service ACTIVITIES, never an instrument's list: which activities a
 * certificate duty reaches, and the verbatim text of each listed item, is the
 * installed pack's `service_list` (one item maps to one value here, with its
 * own text and citation). The organisation states these on the Organization
 * profile page; nothing is pre-filled or inferred from processing activities,
 * vendors, sector, the AI register or installed packs.
 *
 * What the statement does NOT record: whether the organisation holds a
 * certificate or is applying for one. Those are the register's own records
 * (the certificate and the application), so there is one store for each fact.
 */
export const PERSONAL_DATA_PROCESSING_SERVICES = [
    'automated_processing_on_behalf_of_controllers',
    'creditworthiness_scoring',
    'online_collection_and_processing',
    'health_service_data_processing',
    'education_monitoring_data_processing',
    'personal_data_analytics_and_mining',
    'personal_data_encryption',
    'automated_processing_emerging_technologies',
    'location_data_platform',
];
//# sourceMappingURL=org-profile-vocabularies.js.map